/**
 * WebMCP registration — the browser half of `document.modelContext`.
 *
 * The API is young and inconsistent: `registerTool` throws on a duplicate name,
 * and `provideContext` exists in some implementations only. So every call is
 * feature-detected and swallowed — a jobsite renders identically where none of
 * it exists.
 *
 * It also moved. `document` is where the spec put it; `navigator` is the older
 * spelling, deprecated in Chrome 150. Both are still read, `document` first.
 */

/** MCP `CallToolResult` — what an agent receives back from `execute`. */
interface CallToolResult {
  content: { type: "text"; text: string }[];
  isError?: boolean;
}

interface WebMcpTool {
  name: string;
  description: string;
  /** JSON Schema for the tool's arguments. */
  inputSchema?: Record<string, unknown>;
  /** MCP tool annotations — `readOnlyHint` tells an agent it can call freely. */
  annotations?: Record<string, unknown>;
  execute: (input: Record<string, unknown>) => Promise<CallToolResult>;
}

interface ModelContext {
  registerTool?: (tool: WebMcpTool) => unknown;
  unregisterTool?: (name: string) => unknown;
  provideContext?: (init: { tools: WebMcpTool[] }) => unknown;
}

interface WebMcpGlobals {
  modelContext?: ModelContext;
}

/** The live `modelContext`, from wherever this browser exposes it. The two
 *  spellings are the same object where both exist, but preferring the
 *  deprecated one would register where an agent reading the spec's location
 *  isn't looking, should any implementation ever keep them apart. */
const getModelContext = (): ModelContext | null => {
    if (typeof document !== "undefined") {
      const fromDocument = (document as unknown as WebMcpGlobals).modelContext;
      if (fromDocument) {
        return fromDocument;
      }
    }
    if (typeof navigator !== "undefined") {
      const fromNavigator = (navigator as unknown as WebMcpGlobals).modelContext;
      if (fromNavigator) {
        return fromNavigator;
      }
    }
    return null;
  },

  /** Swallow whatever the call returns. The spec's `registerTool` is a promise,
   *  so a refusal — a duplicate name, a frame denied the `tools` permission —
   *  arrives as a rejection that no try/catch around the call can see, and
   *  autocapture reports it as an unhandled error on the visitor's page. */
  swallow = (result: unknown): void => {
    if (typeof (result as { catch?: unknown } | null | undefined)?.catch === "function") {
      (result as Promise<unknown>).catch(() => {});
    }
  },

  /** Register a toolset, returning its teardown. `registerTool` is preferred for
   *  being additive — `provideContext` would drop anything else on the page. */
  registerTools = (tools: WebMcpTool[]): () => void => {
    const context = getModelContext();
    if (!context) {
      return () => {};
    }

    const registered: string[] = [];

    if (typeof context.registerTool === "function") {
      for (const tool of tools) {
        try {
          swallow(context.registerTool(tool));
          registered.push(tool.name);
        } catch {
          // Duplicate name or a schema this implementation rejects. One bad tool
          // Must not cost the page the rest of them.
        }
      }
    } else if (typeof context.provideContext === "function") {
      try {
        swallow(context.provideContext({ tools }));
        registered.push(...tools.map((tool) => tool.name));
      } catch {
        return () => {};
      }
    }

    return () => {
      const live = getModelContext();
      if (typeof live?.unregisterTool !== "function") {
        return;
      }
      for (const name of registered) {
        try {
          swallow(live.unregisterTool(name));
        } catch {
          /* Already gone */
        }
      }
    };
  },

  /** Success: JSON in a text block, which is what MCP clients read. */
  toolResult = (data: unknown): CallToolResult => ({ content: [{ text: JSON.stringify(data, null, 2), type: "text" }], isError: false }),

  /** `details` carries what an agent should offer instead — a number to call, a
   *  page to open — so a refusal is still useful. */
  toolError = (message: string, details?: Record<string, unknown>): CallToolResult => ({
    content: [{ text: JSON.stringify({ error: message, ...details }, null, 2), type: "text" }],
    isError: true,
  });

export type { CallToolResult, WebMcpTool };
export { getModelContext, registerTools, toolResult, toolError };
